/* ============================================================================
   SCOREBORN FLIGHT — SC-1 MCKELVY
   A complete browser flight simulator. Three.js (vendored, MIT) + WebAudio.
   All geometry, textures, and audio are generated procedurally at load time —
   no third-party art assets, nothing hotlinked.
   Systems: World (terrain/ocean/sky/clouds/airfield) · Aircraft · FlightPhysics
   · InputManager · CameraController · HUD · AudioSystem · QualityManager
   ============================================================================ */
(function () {
"use strict";
var root = document.getElementById("sfRoot");
if (!root) return;
var EXIT_URL  = root.dataset.exitUrl  || "/pages/sandbox";
var HOME_URL  = root.dataset.homeUrl  || "/";
var STORE_URL = root.dataset.storeUrl || "/pages/store";

/* ---------------------------------------------------------------- utilities */
function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
function lerp(a, b, t) { return a + (b - a) * t; }
function smoothstep(a, b, x) { var t = clamp((x - a) / (b - a), 0, 1); return t * t * (3 - 2 * t); }
function dampf(cur, target, rate, dt) { return lerp(cur, target, 1 - Math.exp(-rate * dt)); }

/* deterministic value noise (seeded, no assets) */
function makeNoise(seed) {
  var s = seed >>> 0;
  function hash(ix, iz) {
    var h = (ix * 374761393 + iz * 668265263 + s * 1442695041) | 0;
    h = (h ^ (h >>> 13)) | 0; h = (h * 1274126177) | 0;
    return ((h ^ (h >>> 16)) >>> 0) / 4294967295;
  }
  function noise2(x, z) {
    var ix = Math.floor(x), iz = Math.floor(z), fx = x - ix, fz = z - iz;
    var a = hash(ix, iz), b = hash(ix + 1, iz), c = hash(ix, iz + 1), d = hash(ix + 1, iz + 1);
    var ux = fx * fx * (3 - 2 * fx), uz = fz * fz * (3 - 2 * fz);
    return lerp(lerp(a, b, ux), lerp(c, d, ux), uz);
  }
  function fbm(x, z, oct) {
    var v = 0, amp = 0.5, f = 1;
    for (var i = 0; i < oct; i++) { v += amp * noise2(x * f, z * f); amp *= 0.5; f *= 2.03; }
    return v;
  }
  return { noise2: noise2, fbm: fbm, hash: hash };
}
var N = makeNoise(1937);

/* ------------------------------------------------------- world height model */
/* Hand-authored coastal region: a mountain spine to the north-west, rolling
   coastal shelf to the south-east (airfield), a bay with a settlement.        */
var PEAKS = [
  [-800, -2600, 540, 950], [300, -3500, 700, 1150], [1500, -4300, 560, 1050],
  [-1900, -1800, 390, 820], [-3000, -3200, 460, 1000],
  [600, -1500, 180, 900], [-1200, 500, 130, 1050], [2300, -900, 240, 950],
  [3400, -2400, 320, 900]
];
var BASE_CX = 300, BASE_CZ = -1400, BASE_R = 4300, BASE_H = 66;
var CUTS = [[-100, -700, 95, 620], [200, 100, 70, 560]];
var RWY = { x: 1500, z: 900, len: 1250, wid: 46, hgt: 12, pad: 420 };  /* runway strip, E-W */

function heightRaw(x, z) {
  var h = 0, i, p, dx, dz, d2;
  dx = x - BASE_CX; dz = z - BASE_CZ;
  h += BASE_H * Math.exp(-(dx * dx + dz * dz) / (2 * BASE_R * BASE_R));
  for (i = 0; i < PEAKS.length; i++) {
    p = PEAKS[i]; dx = x - p[0]; dz = z - p[1]; d2 = dx * dx + dz * dz;
    h += p[2] * Math.exp(-d2 / (2 * p[3] * p[3]));
  }
  for (i = 0; i < CUTS.length; i++) {
    p = CUTS[i]; dx = x - p[0]; dz = z - p[1]; d2 = dx * dx + dz * dz;
    h -= p[2] * Math.exp(-d2 / (2 * p[3] * p[3]));
  }
  /* rolling detail only where there is land */
  var landAmt = smoothstep(6, 60, h);
  if (landAmt > 0.001) {
    h += (N.fbm(x * 0.00085, z * 0.00085, 4) - 0.47) * 150 * landAmt;
    h += (N.fbm(x * 0.006, z * 0.006, 2) - 0.5) * 14 * landAmt;
  }
  h -= 26;                       /* sea level bias — coastline forms at sum=26 */
  /* distant procedural islands for the horizon (outside main region) */
  var rr = Math.sqrt(x * x + z * z);
  if (rr > 9000) {
    var far = (N.fbm(x * 0.00012 + 7.3, z * 0.00012 - 2.1, 3) - 0.62) * 2200;
    h = Math.max(h, far);
  }
  return h;
}
function runwayMask(x, z) {
  var dx = Math.abs(x - RWY.x), dz = Math.abs(z - RWY.z);
  return (1 - smoothstep(RWY.len / 2, RWY.len / 2 + RWY.pad, dx)) *
         (1 - smoothstep(RWY.wid * 3.4, RWY.wid * 3.4 + RWY.pad, dz));
}
function heightAt(x, z) {
  var h = heightRaw(x, z);
  var m = runwayMask(x, z);
  if (m > 0.001) h = lerp(h, RWY.hgt, m);
  return h;
}
function onRunway(x, z) {
  return Math.abs(x - RWY.x) < RWY.len / 2 + 40 && Math.abs(z - RWY.z) < RWY.wid * 1.6;
}
function terrainNormal(x, z) {
  var e = 6;
  var hx = heightAt(x + e, z) - heightAt(x - e, z);
  var hz = heightAt(x, z + e) - heightAt(x, z - e);
  var n = new THREE.Vector3(-hx, 2 * e, -hz); n.normalize(); return n;
}

/* ------------------------------------------------------------- settings/QM */
var SETTINGS_KEY = "stanhope_fs_v1";
var settings = { quality: "auto", sound: true, sens: 1.0, assist: true, cam: "chase", helpSeen: false, reducedFx: false, actionCam: true };
try {
  var sv = JSON.parse(localStorage.getItem(SETTINGS_KEY) || "{}");
  if (sv && typeof sv === "object") {
    ["quality","sound","sens","assist","cam","helpSeen","reducedFx","actionCam"].forEach(function (k) {
      if (k in sv) settings[k] = sv[k];
    });
    settings.sens = clamp(+settings.sens || 1, 0.4, 2);
    if (["auto","low","medium","high","ultra"].indexOf(settings.quality) < 0) settings.quality = "auto";
    if (["cockpit","chase","orbit","character"].indexOf(settings.cam) < 0) settings.cam = "chase";
  }
} catch (e) { /* invalid saved settings — defaults stand */ }
function saveSettings() { try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings)); } catch (e) {} }

var PRESETS = {
  low:    { scale: 0.62, shadow: 0,    trees: 0.28, clouds: 0.4, aniso: 1, far: 30000 },
  medium: { scale: 0.8,  shadow: 1024, trees: 0.55, clouds: 0.7, aniso: 2, far: 42000 },
  high:   { scale: 1.0,  shadow: 2048, trees: 1.0,  clouds: 1.0, aniso: 4, far: 60000 },
  ultra:  { scale: 1.0,  shadow: 4096, trees: 1.35, clouds: 1.3, aniso: 8, far: 78000 }
};
function detectQuality() {
  try {
    var c = document.createElement("canvas");
    var gl = c.getContext("webgl2") || c.getContext("webgl");
    if (!gl) return "low";
    var gpu = "";
    var dbg = gl.getExtension("WEBGL_debug_renderer_info");
    if (dbg) gpu = String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) || "").toLowerCase();
    var cores = navigator.hardwareConcurrency || 4;
    if (/(apple m|rtx|radeon pro|geforce gtx 16|geforce rtx)/.test(gpu) && cores >= 8) return "high";
    if (/(intel|uhd|iris|mali|adreno|powervr)/.test(gpu) || cores <= 4) return "medium";
    return "high";
  } catch (e) { return "medium"; }
}

/* ------------------------------------------------------------------ UI/DOM */
var UI = {};
function el(tag, cls, parent, html) {
  var e = document.createElement(tag);
  if (cls) e.className = cls;
  if (html != null) e.innerHTML = html;
  (parent || root).appendChild(e); return e;
}
function buildUI() {
  var css = el("style", null, document.head);
  css.textContent = [
    "#sfRoot{--ivory:#e8e0d0;--dim:#9a9488;--gold:#c9b99a;--warn:#ff5b4d;color:var(--ivory)}",
    ".sf-layer{position:absolute;inset:0;pointer-events:none}",
    ".sf-load{display:flex;flex-direction:column;align-items:center;justify-content:center;background:#05070a;pointer-events:auto;z-index:30;transition:opacity .5s}",
    ".sf-load .t{font:400 30px Georgia,serif;letter-spacing:.06em;margin-bottom:6px}",
    ".sf-load .s{font:11px ui-monospace,monospace;letter-spacing:.4em;color:var(--gold);margin-bottom:38px}",
    ".sf-bar{width:min(380px,72vw);height:2px;background:#22251f;position:relative;overflow:hidden}",
    ".sf-bar i{position:absolute;inset:0;transform-origin:0 50%;background:var(--gold);transform:scaleX(0);transition:transform .25s}",
    ".sf-stage{margin-top:14px;font:10px ui-monospace,monospace;letter-spacing:.28em;color:var(--dim)}",
    ".sf-title{display:flex;flex-direction:column;align-items:center;justify-content:center;background:rgba(5,7,10,.0);z-index:29;opacity:0;transition:opacity .6s}",
    ".sf-title .t{font:400 44px Georgia,serif;text-shadow:0 2px 30px rgba(0,0,0,.8)}",
    ".sf-title .s{font:11px ui-monospace,monospace;letter-spacing:.5em;color:var(--gold);margin:10px 0 26px}",
    ".sf-title .k{font:11px ui-monospace,monospace;letter-spacing:.24em;color:var(--ivory);opacity:.85;animation:sfPulse 1.8s ease infinite}",
    "@keyframes sfPulse{50%{opacity:.35}}",
    ".sf-hud{font:12px ui-monospace,monospace;letter-spacing:.06em;text-shadow:0 1px 3px rgba(0,0,0,.9),0 0 12px rgba(0,0,0,.5);z-index:10}",
    ".sf-strip{position:absolute;top:16px;left:50%;transform:translateX(-50%);display:flex;gap:26px;background:rgba(5,8,12,.42);border:1px solid rgba(255,255,255,.07);padding:9px 20px;border-radius:3px;backdrop-filter:blur(3px)}",
    ".sf-strip b{font-weight:400;color:var(--dim);font-size:9px;letter-spacing:.22em;display:block;margin-bottom:2px}",
    ".sf-strip span{font-size:15px;color:var(--ivory);font-variant-numeric:tabular-nums}",
    ".sf-chips{position:absolute;right:16px;top:16px;display:flex;flex-direction:column;gap:6px;align-items:flex-end}",
    ".sf-chip{background:rgba(5,8,12,.42);border:1px solid rgba(255,255,255,.07);padding:5px 10px;border-radius:3px;font-size:10px;letter-spacing:.2em;color:var(--dim)}",
    ".sf-chip em{font-style:normal;color:var(--ivory)}",
    ".sf-thr{position:absolute;left:50%;bottom:20px;transform:translateX(-50%);width:210px;background:rgba(5,8,12,.42);border:1px solid rgba(255,255,255,.07);padding:8px 12px;border-radius:3px}",
    ".sf-thr b{font-weight:400;color:var(--dim);font-size:9px;letter-spacing:.22em}",
    ".sf-thr .tk{height:3px;background:#242720;margin-top:5px;position:relative}",
    ".sf-thr .tk i{position:absolute;inset:0;transform-origin:0 50%;background:var(--gold)}",
    ".sf-horizon{position:absolute;left:16px;bottom:16px;width:118px;height:118px;border-radius:50%;border:1px solid rgba(255,255,255,.1);overflow:hidden;background:rgba(5,8,12,.3)}",
    ".sf-alert{position:absolute;left:50%;top:34%;transform:translate(-50%,-50%);font-size:17px;letter-spacing:.5em;color:var(--warn);text-shadow:0 0 16px rgba(255,60,40,.6);animation:sfPulse .55s steps(2) infinite}",
    ".sf-center{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;z-index:20;pointer-events:auto;background:rgba(4,6,9,.68);backdrop-filter:blur(4px)}",
    ".sf-panel{background:#0d0f0d;border:1px solid #2a2d26;padding:30px 34px;max-width:480px;width:min(480px,92vw);max-height:86svh;overflow:auto}",
    ".sf-panel h2{font:400 24px Georgia,serif;margin:0 0 6px;color:var(--ivory)}",
    ".sf-panel p{color:var(--dim);font:13px/1.7 ui-monospace,monospace;margin:0 0 16px}",
    ".sf-row{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:10px}",
    ".sf-panel button,.sf-panel a{pointer-events:auto;cursor:pointer;background:#181a15;border:1px solid #34372e;color:var(--ivory);font:11px ui-monospace,monospace;letter-spacing:.18em;padding:11px 16px;text-decoration:none;display:inline-block}",
    ".sf-panel button:hover,.sf-panel a:hover,.sf-panel button:focus-visible,.sf-panel a:focus-visible{border-color:var(--gold);color:var(--gold)}",
    ".sf-panel button[aria-pressed=\"true\"]{background:#2a2d1f;border-color:var(--gold);color:var(--gold)}",
    ".sf-panel .primary{background:var(--ivory);color:#101008;border-color:var(--ivory)}",
    ".sf-panel .primary:hover,.sf-panel .primary:focus-visible{background:var(--gold);color:#101008}",
    ".sf-kbd{width:100%;border-collapse:collapse;font:11px ui-monospace,monospace;color:var(--dim);margin-bottom:14px}",
    ".sf-kbd td{padding:4px 6px;border-bottom:1px solid #1e211b}",
    ".sf-kbd td:first-child{color:var(--ivory);white-space:nowrap;letter-spacing:.1em}",
    ".sf-help{position:absolute;left:16px;top:16px;background:rgba(5,8,12,.55);border:1px solid rgba(255,255,255,.08);padding:12px 16px;border-radius:3px;font-size:10.5px;line-height:2;letter-spacing:.12em;color:var(--dim);z-index:11}",
    ".sf-help b{color:var(--ivory);font-weight:400}",
    ".sf-toast{position:absolute;left:50%;bottom:76px;transform:translateX(-50%);background:rgba(10,12,10,.85);border:1px solid #33362c;color:var(--ivory);font:11px ui-monospace,monospace;letter-spacing:.16em;padding:9px 16px;opacity:0;transition:opacity .3s;z-index:15}",
    "@media (max-width:820px){.sf-strip{gap:14px;padding:7px 12px}.sf-strip span{font-size:12px}.sf-horizon{display:none}}",
    "@media (prefers-reduced-motion:reduce){.sf-alert,.sf-title .k{animation:none}}"
  ].join("\n");

  UI.load = el("div", "sf-layer sf-load", root,
    "<div class='t'>Scoreborn Flight</div><div class='s'>SC-1 · MCKELVY</div>" +
    "<div class='sf-bar' role='progressbar' aria-label='Loading flight simulator' aria-valuemin='0' aria-valuemax='100' aria-valuenow='0'><i></i></div>" +
    "<div class='sf-stage' aria-live='polite'>PREPARING</div>");
  UI.loadBar = UI.load.querySelector(".sf-bar i");
  UI.loadPB  = UI.load.querySelector(".sf-bar");
  UI.loadStage = UI.load.querySelector(".sf-stage");

  UI.title = el("div", "sf-layer sf-title", root,
    "<div class='t'>Scoreborn Flight</div><div class='s'>THE COAST · SC-1 MCKELVY</div>" +
    "<div class='k'>PRESS ANY KEY TO TAKE THE CONTROLS</div>");

  UI.hud = el("div", "sf-layer sf-hud", root);
  UI.strip = el("div", "sf-strip", UI.hud,
    "<div><b>AIRSPEED</b><span id='sfIas'>0</span> <b style='display:inline'>KT</b></div>" +
    "<div><b>ALTITUDE</b><span id='sfAlt'>0</span> <b style='display:inline'>FT</b></div>" +
    "<div><b>HEADING</b><span id='sfHdg'>0</span></div>" +
    "<div><b>V/S</b><span id='sfVsi'>0</span> <b style='display:inline'>FPM</b></div>");
  UI.chips = el("div", "sf-chips", UI.hud,
    "<div class='sf-chip'>ASSIST <em id='sfAssist'>ON</em></div>" +
    "<div class='sf-chip'>CAM <em id='sfCam'>CHASE</em></div>" +
    "<div class='sf-chip' id='sfMuteChip'>SOUND <em id='sfSnd'>ON</em></div>");
  UI.thr = el("div", "sf-thr", UI.hud, "<b>THROTTLE</b><div class='tk'><i id='sfThr'></i></div>");
  UI.horizon = el("canvas", "sf-horizon", UI.hud);
  UI.horizon.width = UI.horizon.height = 118;
  UI.alert = el("div", "sf-alert", UI.hud, ""); UI.alert.style.display = "none";
  UI.toast = el("div", "sf-toast", root, "");
  UI.help = el("div", "sf-help", root,
    "<b>W/S</b> pitch · <b>A/D</b> roll · <b>Q/E</b> yaw<br><b>SHIFT/CTRL</b> throttle · <b>C</b> camera · <b>R</b> reset<br><b>P</b> pause · <b>H</b> hide this · <b>M</b> sound · <b>F</b> fullscreen");
  UI.help.style.display = "none";

  UI.ias = document.getElementById("sfIas"); UI.altEl = document.getElementById("sfAlt");
  UI.hdg = document.getElementById("sfHdg"); UI.vsi = document.getElementById("sfVsi");
  UI.thrBar = document.getElementById("sfThr");
  UI.assistChip = document.getElementById("sfAssist"); UI.camChip = document.getElementById("sfCam");
  UI.sndChip = document.getElementById("sfSnd");
  UI.hud.style.display = "none";
}
var toastT = null;
function toast(msg, ms) {
  UI.toast.textContent = msg; UI.toast.style.opacity = "1";
  clearTimeout(toastT); toastT = setTimeout(function () { UI.toast.style.opacity = "0"; }, ms || 1800);
}

/* modal manager with focus trap + restoration */
var modal = { node: null, prevFocus: null };
function openModal(html, labelledBy) {
  closeModal();
  modal.prevFocus = document.activeElement;
  modal.node = el("div", "sf-center", root);
  modal.node.setAttribute("role", "dialog");
  modal.node.setAttribute("aria-modal", "true");
  if (labelledBy) modal.node.setAttribute("aria-label", labelledBy);
  var p = el("div", "sf-panel", modal.node, html);
  var f = p.querySelector("button,a"); if (f) f.focus();
  modal.node.addEventListener("keydown", function (ev) {
    if (ev.key !== "Tab") return;
    var els = p.querySelectorAll("button,a,select,[tabindex]");
    if (!els.length) return;
    var first = els[0], last = els[els.length - 1];
    if (ev.shiftKey && document.activeElement === first) { ev.preventDefault(); last.focus(); }
    else if (!ev.shiftKey && document.activeElement === last) { ev.preventDefault(); first.focus(); }
  });
  return p;
}
function closeModal() {
  if (modal.node) {
    modal.node.remove(); modal.node = null;
    if (modal.prevFocus && modal.prevFocus.focus) try { modal.prevFocus.focus(); } catch (e) {}
  }
}
function fatal(title, msg) {
  root.innerHTML = "";
  var d = el("div", "sf-center", root);
  d.style.background = "#05070a";
  el("div", "sf-panel", d,
    "<h2>" + title + "</h2><p>" + msg + "</p>" +
    "<div class='sf-row'><a href='" + EXIT_URL + "'>RETURN TO SANDBOX</a>" +
    "<a href='" + HOME_URL + "'>GATEWAY</a></div>");
}

/* ------------------------------------------------------------ render setup */
var renderer, scene, camera, sunLight, hemi, skyMesh, envRT;
var quality, qKey, disposables = [];
var SUN_DIR = new THREE.Vector3(-0.45, 0.52, -0.72).normalize();

function track(o) { disposables.push(o); return o; }

function initRenderer() {
  renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: "high-performance" });
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  root.appendChild(renderer.domElement);
  renderer.domElement.setAttribute("aria-label", "Flight simulator viewport");
  scene = new THREE.Scene();
  scene.fog = new THREE.FogExp2(0xbccfd8, 0.000042);
  camera = new THREE.PerspectiveCamera(62, 1, 0.5, 90000);
  sunLight = new THREE.DirectionalLight(0xfff2dd, 3.1);
  sunLight.position.copy(SUN_DIR).multiplyScalar(9000);
  scene.add(sunLight);
  hemi = new THREE.HemisphereLight(0xb8d2e8, 0x51574a, 0.75);
  scene.add(hemi);
  applyQuality(settings.quality === "auto" ? detectQuality() : settings.quality, true);
}
function applyQuality(key, silent) {
  qKey = key; quality = PRESETS[key] || PRESETS.medium;
  var pr = Math.min(devicePixelRatio || 1, 2) * quality.scale;
  renderer.setPixelRatio(pr);
  resize();
  renderer.shadowMap.enabled = quality.shadow > 0;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  if (quality.shadow > 0) {
    sunLight.castShadow = true;
    sunLight.shadow.mapSize.set(quality.shadow, quality.shadow);
    var sc = sunLight.shadow.camera;
    sc.near = 2000; sc.far = 16000;
    sc.left = -900; sc.right = 900; sc.top = 900; sc.bottom = -900;
    sunLight.shadow.bias = -0.0006;
    if (sunLight.shadow.map) { sunLight.shadow.map.dispose(); sunLight.shadow.map = null; }
  } else { sunLight.castShadow = false; }
  camera.far = quality.far; camera.updateProjectionMatrix();
  if (!silent) toast("QUALITY: " + key.toUpperCase());
}
function resize() {
  var w = root.clientWidth || innerWidth, h = root.clientHeight || innerHeight;
  renderer.setSize(w, h, false);
  renderer.domElement.style.width = "100%"; renderer.domElement.style.height = "100%";
  camera.aspect = w / h; camera.updateProjectionMatrix();
}

/* --------------------------------------------------------------------- sky */
function buildSky() {
  var geo = track(new THREE.SphereGeometry(46000, 32, 18));
  var mat = track(new THREE.ShaderMaterial({
    side: THREE.BackSide, depthWrite: false, fog: false,
    uniforms: { sunDir: { value: SUN_DIR } },
    vertexShader:
      "varying vec3 vDir; void main(){ vDir=normalize(position); gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0); }",
    fragmentShader:
      "varying vec3 vDir; uniform vec3 sunDir;\n" +
      "void main(){\n" +
      " float h=clamp(vDir.y,-0.12,1.0);\n" +
      " vec3 zen=vec3(0.16,0.34,0.62); vec3 mid=vec3(0.55,0.72,0.86); vec3 hor=vec3(0.86,0.88,0.86);\n" +
      " vec3 col=mix(hor,mid,smoothstep(0.0,0.18,h)); col=mix(col,zen,smoothstep(0.12,0.75,h));\n" +
      " float s=max(dot(vDir,sunDir),0.0);\n" +
      " col+=vec3(1.0,0.86,0.62)*pow(s,700.0)*8.0;\n" +
      " col+=vec3(1.0,0.82,0.55)*pow(s,18.0)*0.28;\n" +
      " col+=vec3(0.92,0.9,0.82)*pow(1.0-abs(vDir.y),6.0)*0.22;\n" +
      " if(vDir.y<0.0) col=mix(col,vec3(0.34,0.44,0.5),smoothstep(0.0,-0.1,vDir.y));\n" +
      " gl_FragColor=vec4(col,1.0);\n}"
  }));
  skyMesh = new THREE.Mesh(geo, mat);
  skyMesh.frustumCulled = false;
  scene.add(skyMesh);
  /* environment map for PBR reflections, generated once from the sky */
  var pm = new THREE.PMREMGenerator(renderer);
  var envScene = new THREE.Scene();
  envScene.add(new THREE.Mesh(geo.clone(), mat.clone()));
  envRT = pm.fromScene(envScene, 0.02);
  scene.environment = envRT.texture;
  pm.dispose();
}

/* ------------------------------------------------------------------ terrain */
var terrainMeshes = [];
function buildTerrainGrid(size, segs, drop) {
  var geo = track(new THREE.PlaneGeometry(size, size, segs, segs));
  geo.rotateX(-Math.PI / 2);
  var pos = geo.attributes.position;
  var colors = new Float32Array(pos.count * 3);
  var cGrass = new THREE.Color(0x59713f), cDry = new THREE.Color(0x8b8a56),
      cRock = new THREE.Color(0x6d6a63), cSand = new THREE.Color(0xcdbd96),
      cSnow = new THREE.Color(0xe8ecee), cDark = new THREE.Color(0x3c5233), tmp = new THREE.Color();
  /* precompute the height field once; slopes come from grid neighbours */
  var W = segs + 1, hf = new Float32Array(W * W), i;
  for (i = 0; i < pos.count; i++) hf[i] = heightAt(pos.getX(i), pos.getZ(i));
  var cell = size / segs;
  for (i = 0; i < pos.count; i++) {
    var x = pos.getX(i), z = pos.getZ(i);
    var h = hf[i];
    pos.setY(i, h - (drop || 0));
    var cx = i % W, cz = (i / W) | 0;
    var xm = hf[cz * W + Math.max(cx - 1, 0)], xp = hf[cz * W + Math.min(cx + 1, W - 1)];
    var zm = hf[Math.max(cz - 1, 0) * W + cx], zp = hf[Math.min(cz + 1, W - 1) * W + cx];
    var slope = Math.sqrt((xp - xm) * (xp - xm) + (zp - zm) * (zp - zm)) / (2 * cell);
    var nz = N.fbm(x * 0.0016, z * 0.0016, 3);
    tmp.copy(cGrass).lerp(cDark, smoothstep(0.35, 0.7, nz));
    tmp.lerp(cDry, smoothstep(0.5, 0.75, N.fbm(x * 0.0007 + 11.0, z * 0.0007, 3)) * 0.7);
    tmp.lerp(cRock, smoothstep(0.42, 0.85, slope));
    if (h < 4) tmp.copy(cSand).lerp(cRock, smoothstep(0.5, 1.2, slope));
    if (h > 300) tmp.lerp(cRock, smoothstep(300, 440, h) * 0.9);
    if (h > 520) tmp.lerp(cSnow, smoothstep(520, 640, h + nz * 70));
    colors[i * 3] = tmp.r; colors[i * 3 + 1] = tmp.g; colors[i * 3 + 2] = tmp.b;
  }
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  geo.computeVertexNormals();
  var mat = track(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.94, metalness: 0 }));
  var m = new THREE.Mesh(geo, mat);
  m.receiveShadow = quality.shadow > 0;
  scene.add(m); terrainMeshes.push(m);
  return m;
}
function buildTerrain() {
  buildTerrainGrid(13000, 200, 0);          /* island detail */
  buildTerrainGrid(64000, 110, 3.0);        /* horizon ring, tucked 3m below */
}

/* -------------------------------------------------------------------- ocean */
var oceanMat, oceanMesh;
function buildOcean() {
  var SIZE = 26000, SEG = 150;
  var geo = track(new THREE.PlaneGeometry(SIZE, SIZE, SEG, SEG));
  geo.rotateX(-Math.PI / 2);
  var pos = geo.attributes.position, depth = new Float32Array(pos.count);
  for (var i = 0; i < pos.count; i++) {
    var x = pos.getX(i), z = pos.getZ(i);
    /* shore accuracy only matters near the island; deep water is deep water */
    depth[i] = (Math.abs(x) < 8500 && Math.abs(z) < 8500) ? clamp(-heightRaw(x, z), 0, 60) : 60;
  }
  geo.setAttribute("aDepth", new THREE.BufferAttribute(depth, 1));
  oceanMat = track(new THREE.ShaderMaterial({
    transparent: false, fog: true,
    uniforms: THREE.UniformsUtils.merge([THREE.UniformsLib.fog, {
      uTime: { value: 0 }, sunDir: { value: SUN_DIR },
      uCam: { value: new THREE.Vector3() }
    }]),
    vertexShader:
      "attribute float aDepth; varying float vDepth; varying vec3 vWorld;\n" +
      "uniform float uTime;\n#include <fog_pars_vertex>\n" +
      "void main(){ vDepth=aDepth; vec3 p=position;\n" +
      " float sh=clamp(aDepth/7.0,0.15,1.0);\n" +
      " p.y+=sin(p.x*0.011+uTime*0.9)*0.55*sh + sin(p.z*0.014+uTime*1.17)*0.45*sh + sin((p.x+p.z)*0.0042+uTime*0.53)*0.8*sh;\n" +
      " vec4 wp=modelMatrix*vec4(p,1.0); vWorld=wp.xyz;\n" +
      " vec4 mvPosition=viewMatrix*wp; gl_Position=projectionMatrix*mvPosition;\n" +
      " #include <fog_vertex>\n}",
    fragmentShader:
      "varying float vDepth; varying vec3 vWorld;\n" +
      "uniform float uTime; uniform vec3 sunDir; uniform vec3 uCam;\n#include <fog_pars_fragment>\n" +
      "float hash(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}\n" +
      "void main(){\n" +
      " float nx=sin(vWorld.x*0.017+uTime*1.1)*0.5+sin(vWorld.z*0.023+uTime*0.7)*0.5;\n" +
      " float nz=sin(vWorld.z*0.019+uTime*0.95)*0.5+sin((vWorld.x-vWorld.z)*0.013+uTime*0.6)*0.5;\n" +
      " vec3 nrm=normalize(vec3(nx*0.22,1.0,nz*0.22));\n" +
      " vec3 view=normalize(uCam-vWorld);\n" +
      " vec3 shallow=vec3(0.13,0.42,0.45); vec3 deep=vec3(0.035,0.13,0.24);\n" +
      " vec3 col=mix(shallow,deep,smoothstep(1.0,26.0,vDepth));\n" +
      " float fres=pow(1.0-max(dot(view,nrm),0.0),3.0);\n" +
      " col=mix(col,vec3(0.56,0.68,0.78),fres*0.55);\n" +
      " vec3 hv=normalize(view+sunDir);\n" +
      " col+=vec3(1.0,0.9,0.7)*pow(max(dot(nrm,hv),0.0),300.0)*1.4;\n" +
      " float foam=smoothstep(2.6,0.35,vDepth)*(0.55+0.45*sin(uTime*1.7+vWorld.x*0.09+vWorld.z*0.07));\n" +
      " foam*=0.5+0.5*hash(floor(vWorld.xz*0.6));\n" +
      " col=mix(col,vec3(0.9,0.94,0.92),clamp(foam,0.0,0.85));\n" +
      " gl_FragColor=vec4(col,1.0);\n #include <fog_fragment>\n}"
  }));
  oceanMesh = new THREE.Mesh(geo, oceanMat);
  oceanMesh.position.y = 0.0;
  scene.add(oceanMesh);
  /* far ocean disk out to the horizon */
  var farGeo = track(new THREE.RingGeometry(12800, 95000, 48, 1));
  farGeo.rotateX(-Math.PI / 2);
  var farMat = track(new THREE.MeshStandardMaterial({ color: 0x0a2036, roughness: 0.4, metalness: 0.1 }));
  var far = new THREE.Mesh(farGeo, farMat);
  far.position.y = -0.4; scene.add(far);
}

/* ------------------------------------------------------------------- forest */
function buildForest() {
  var count = Math.floor(9000 * quality.trees);
  /* one merged tree = trunk + two canopy cones; instanced once */
  var trunk = new THREE.CylinderGeometry(0.28, 0.42, 3.2, 5); trunk.translate(0, 1.6, 0);
  var c1 = new THREE.ConeGeometry(2.6, 5.4, 6); c1.translate(0, 5.4, 0);
  var c2 = new THREE.ConeGeometry(1.8, 3.8, 6); c2.translate(0, 8.2, 0);
  function mergeGeos(list) {
    var expanded = list.map(function (g) { return g.index ? g.toNonIndexed() : g; });
    var total = 0; expanded.forEach(function (g) { total += g.attributes.position.count; });
    var posA = new Float32Array(total * 3), nrmA = new Float32Array(total * 3), off = 0;
    expanded.forEach(function (g) {
      posA.set(g.attributes.position.array, off * 3);
      nrmA.set(g.attributes.normal.array, off * 3);
      off += g.attributes.position.count;
    });
    var out = new THREE.BufferGeometry();
    out.setAttribute("position", new THREE.BufferAttribute(posA, 3));
    out.setAttribute("normal", new THREE.BufferAttribute(nrmA, 3));
    return out;
  }
  var tree = track(mergeGeos([trunk, c1, c2]));
  var mat = track(new THREE.MeshStandardMaterial({ color: 0x2e4a28, roughness: 0.95 }));
  var inst = new THREE.InstancedMesh(tree, mat, count);
  inst.castShadow = quality.shadow > 0;
  var dummy = new THREE.Object3D(), col = new THREE.Color(), placed = 0, guard = 0;
  while (placed < count && guard < count * 12) {
    guard++;
    var x = (N.hash(guard * 7 + 1, guard * 13 + 5) - 0.5) * 11800;
    var z = (N.hash(guard * 3 + 9, guard * 11 + 2) - 0.5) * 11800;
    if (N.noise2(x * 0.0011 + 4.7, z * 0.0011 - 2.2) < 0.52) continue;   /* cheap mask first */
    var h = heightAt(x, z);
    if (h < 7 || h > 380) continue;
    if (runwayMask(x, z) > 0.02) continue;
    var dh = Math.abs(heightAt(x + 9, z) - h) + Math.abs(heightAt(x, z + 9) - h);
    if (dh > 7.5) continue;                                              /* too steep */
    var s = 0.7 + N.hash(guard, 17) * 0.9;
    dummy.position.set(x, h - 0.2, z);
    dummy.scale.set(s, s * (0.85 + N.hash(guard, 23) * 0.5), s);
    dummy.rotation.y = N.hash(guard, 31) * 6.28;
    dummy.updateMatrix();
    inst.setMatrixAt(placed, dummy.matrix);
    col.setHSL(0.29 + N.hash(guard, 41) * 0.05, 0.42, 0.2 + N.hash(guard, 43) * 0.1);
    inst.setColorAt(placed, col);
    placed++;
  }
  inst.count = placed;
  inst.instanceMatrix.needsUpdate = true;
  if (inst.instanceColor) inst.instanceColor.needsUpdate = true;
  scene.add(inst); track(inst.geometry);
}

/* ----------------------------------------------------------------- airfield */
function canvasTex(w, h, draw) {
  var c = document.createElement("canvas"); c.width = w; c.height = h;
  draw(c.getContext("2d"), w, h);
  var t = new THREE.CanvasTexture(c);
  t.anisotropy = quality.aniso; t.colorSpace = THREE.SRGBColorSpace;
  return track(t);
}
function buildAirfield() {
  var g = new THREE.Group();
  /* runway */
  var rwyTex = canvasTex(1024, 128, function (x) {
    x.fillStyle = "#3c3f42"; x.fillRect(0, 0, 1024, 128);
    for (var i = 0; i < 900; i++) { x.fillStyle = "rgba(0,0,0," + (Math.random() * 0.08) + ")"; x.fillRect(Math.random() * 1024, Math.random() * 128, 3, 3); }
    x.fillStyle = "#cfd3d6";
    for (var d = 70; d < 954; d += 56) x.fillRect(d, 61, 30, 6);       /* centerline */
    x.fillRect(6, 10, 4, 108); x.fillRect(1014, 10, 4, 108);           /* edges */
    for (var t2 = 0; t2 < 8; t2++) { x.fillRect(22, 16 + t2 * 12, 34, 7); x.fillRect(968, 16 + t2 * 12, 34, 7); }
    x.font = "bold 34px monospace"; x.fillStyle = "#e3e6e8";
    x.save(); x.translate(96, 64); x.rotate(Math.PI / 2); x.textAlign = "center"; x.fillText("27", 0, 10); x.restore();
    x.save(); x.translate(928, 64); x.rotate(-Math.PI / 2); x.textAlign = "center"; x.fillText("09", 0, 10); x.restore();
  });
  var rwy = new THREE.Mesh(
    track(new THREE.PlaneGeometry(RWY.len, RWY.wid)),
    track(new THREE.MeshStandardMaterial({ map: rwyTex, roughness: 0.92 })));
  rwy.rotation.x = -Math.PI / 2;
  rwy.position.set(RWY.x, RWY.hgt + 0.06, RWY.z);
  rwy.receiveShadow = quality.shadow > 0;
  g.add(rwy);
  /* apron */
  var apron = new THREE.Mesh(
    track(new THREE.PlaneGeometry(260, 190)),
    track(new THREE.MeshStandardMaterial({ color: 0x484c50, roughness: 0.95 })));
  apron.rotation.x = -Math.PI / 2;
  apron.position.set(RWY.x - 250, RWY.hgt + 0.05, RWY.z + 150);
  g.add(apron);
  /* hangars */
  var hangarMat = track(new THREE.MeshStandardMaterial({ color: 0x9aa0a4, roughness: 0.6, metalness: 0.35 }));
  var roofMat = track(new THREE.MeshStandardMaterial({ color: 0x77592f, roughness: 0.7, metalness: 0.2 }));
  for (var i = 0; i < 2; i++) {
    var hg = new THREE.Group();
    var body = new THREE.Mesh(track(new THREE.BoxGeometry(46, 12, 34)), hangarMat);
    body.position.y = 6; hg.add(body);
    var roof = new THREE.Mesh(track(new THREE.CylinderGeometry(17, 17, 46, 12, 1, false, 0, Math.PI)), roofMat);
    roof.rotation.z = Math.PI / 2; roof.rotation.x = Math.PI; roof.position.y = 12; hg.add(roof);
    hg.position.set(RWY.x - 250 + i * 60, RWY.hgt, RWY.z + 235);
    hg.traverse(function (o) { if (o.isMesh) { o.castShadow = quality.shadow > 0; o.receiveShadow = true; } });
    g.add(hg);
  }
  /* tower */
  var tower = new THREE.Group();
  var shaft = new THREE.Mesh(track(new THREE.CylinderGeometry(3, 3.6, 20, 8)), hangarMat);
  shaft.position.y = 10; tower.add(shaft);
  var cab = new THREE.Mesh(track(new THREE.CylinderGeometry(5.4, 4.6, 5, 8)),
    track(new THREE.MeshStandardMaterial({ color: 0x1c2b33, roughness: 0.15, metalness: 0.6 })));
  cab.position.y = 22.5; tower.add(cab);
  tower.position.set(RWY.x - 140, RWY.hgt, RWY.z + 230);
  tower.traverse(function (o) { if (o.isMesh) o.castShadow = quality.shadow > 0; });
  g.add(tower);
  /* windsock */
  var pole = new THREE.Mesh(track(new THREE.CylinderGeometry(0.16, 0.16, 9, 6)),
    track(new THREE.MeshStandardMaterial({ color: 0xcccccc })));
  pole.position.set(RWY.x + 430, RWY.hgt + 4.5, RWY.z + 60); g.add(pole);
  var sock = new THREE.Mesh(track(new THREE.ConeGeometry(0.9, 4.4, 8, 1, true)),
    track(new THREE.MeshStandardMaterial({ color: 0xe06a1f, side: THREE.DoubleSide })));
  sock.rotation.z = Math.PI / 2; sock.position.set(RWY.x + 432.6, RWY.hgt + 8.6, RWY.z + 60); g.add(sock);
  /* settlement by the bay — instanced houses */
  var houses = 26;
  var hMat = track(new THREE.MeshStandardMaterial({ roughness: 0.8 }));
  var houseGeo = track(new THREE.BoxGeometry(9, 6, 8));
  var hInst = new THREE.InstancedMesh(houseGeo, hMat, houses);
  var d2 = new THREE.Object3D(), hc = new THREE.Color(), got = 0, tries = 0;
  while (got < houses && tries < 900) {
    tries++;
    var hx = -650 + (N.hash(tries, 91) - 0.5) * 900;
    var hz = 900 + (N.hash(tries, 97) - 0.5) * 700;
    var hh = heightAt(hx, hz);
    if (hh < 6 || hh > 60) continue;
    if (terrainNormal(hx, hz).y < 0.93) continue;
    d2.position.set(hx, hh + 2.6, hz);
    d2.rotation.y = N.hash(tries, 5) * 6.28;
    var hs = 0.8 + N.hash(tries, 7) * 0.7; d2.scale.set(hs, hs, hs);
    d2.updateMatrix(); hInst.setMatrixAt(got, d2.matrix);
    hc.setHSL(0.08 + N.hash(tries, 11) * 0.05, 0.25, 0.55 + N.hash(tries, 13) * 0.2);
    hInst.setColorAt(got, hc); got++;
  }
  hInst.count = got; hInst.instanceMatrix.needsUpdate = true;
  hInst.castShadow = quality.shadow > 0;
  g.add(hInst);
  scene.add(g);
}

/* ------------------------------------------------------------------- clouds */
var cloudGroup, cloudSprites = [];
function buildClouds() {
  cloudGroup = new THREE.Group();
  var puffTex = canvasTex(256, 256, function (x) {
    var grd = x.createRadialGradient(128, 128, 8, 128, 128, 126);
    grd.addColorStop(0, "rgba(255,255,255,.95)");
    grd.addColorStop(0.45, "rgba(248,250,252,.55)");
    grd.addColorStop(1, "rgba(255,255,255,0)");
    x.fillStyle = grd; x.fillRect(0, 0, 256, 256);
    /* break up the edge */
    x.globalCompositeOperation = "destination-out";
    for (var i = 0; i < 46; i++) {
      var a = Math.random() * 6.28, r = 96 + Math.random() * 34;
      var gx = 128 + Math.cos(a) * r, gy = 128 + Math.sin(a) * r;
      var g2 = x.createRadialGradient(gx, gy, 0, gx, gy, 26 + Math.random() * 22);
      g2.addColorStop(0, "rgba(0,0,0,.5)"); g2.addColorStop(1, "rgba(0,0,0,0)");
      x.fillStyle = g2; x.beginPath(); x.arc(gx, gy, 52, 0, 6.29); x.fill();
    }
  });
  var mat = track(new THREE.SpriteMaterial({ map: puffTex, transparent: true, opacity: 0.85, depthWrite: false, fog: true }));
  var clusters = Math.floor(24 * quality.clouds);
  for (var c = 0; c < clusters; c++) {
    var cx = (N.hash(c, 301) - 0.5) * 22000;
    var cz = (N.hash(c, 307) - 0.5) * 22000;
    var cy = 750 + N.hash(c, 311) * 950;
    var puffs = 4 + Math.floor(N.hash(c, 313) * 5);
    for (var p2 = 0; p2 < puffs; p2++) {
      var sp = new THREE.Sprite(mat);
      sp.position.set(
        cx + (N.hash(c * 31 + p2, 17) - 0.5) * 620,
        cy + (N.hash(c * 31 + p2, 19) - 0.5) * 130,
        cz + (N.hash(c * 31 + p2, 23) - 0.5) * 620);
      var ss = 260 + N.hash(c * 31 + p2, 29) * 340;
      sp.scale.set(ss, ss * 0.52, 1);
      cloudGroup.add(sp); cloudSprites.push(sp);
    }
  }
  /* distant horizon banks */
  for (var b = 0; b < 12; b++) {
    var ang = b / 12 * Math.PI * 2;
    var sp2 = new THREE.Sprite(track(new THREE.SpriteMaterial({ map: puffTex, transparent: true, opacity: 0.4, depthWrite: false, fog: false })));
    sp2.position.set(Math.cos(ang) * 34000, 1500 + N.hash(b, 41) * 900, Math.sin(ang) * 34000);
    sp2.scale.set(9000, 2400, 1);
    cloudGroup.add(sp2);
  }
  scene.add(cloudGroup);
}

/* ----------------------------------------------------------------- aircraft */
/* Original design: Scoreborn SC-1 "Mckelvy" — a light jet trainer.
   Body frame: nose toward -Z, +Y up, +X to the pilot's right.               */
var aircraft, acParts = {};
function buildAircraft() {
  aircraft = new THREE.Group();
  var model = new THREE.Group();
  aircraft.add(model); acParts.model = model;

  var paint = track(new THREE.MeshStandardMaterial({ color: 0xe9e6df, roughness: 0.32, metalness: 0.12, envMapIntensity: 0.9 }));
  var navy  = track(new THREE.MeshStandardMaterial({ color: 0x14243d, roughness: 0.36, metalness: 0.15 }));
  var dark  = track(new THREE.MeshStandardMaterial({ color: 0x22252a, roughness: 0.55, metalness: 0.4 }));

  /* fuselage — lathe profile, nose at -Z */
  var prof = [
    [0.00, 0.0], [0.30, 0.16], [0.9, 0.34], [1.8, 0.5], [2.8, 0.6],
    [4.2, 0.62], [5.6, 0.56], [7.0, 0.44], [8.2, 0.3], [9.2, 0.16], [9.5, 0.1]
  ].map(function (p) { return new THREE.Vector2(p[1], p[0]); });
  var fusGeo = track(new THREE.LatheGeometry(prof, 28));
  fusGeo.rotateX(-Math.PI / 2);            /* axis +Y -> -Z; nose(y=0) at z=0, tail at z=-9.5 */
  fusGeo.rotateY(Math.PI);                 /* flip: nose to -Z, tail to +Z */
  fusGeo.translate(0, 0, -3.9);            /* centre of mass near origin */
  var fus = new THREE.Mesh(fusGeo, paint); model.add(fus); acParts.fuselage = fus;

  /* canopy */
  var canGeo = track(new THREE.SphereGeometry(0.58, 20, 14, 0, Math.PI * 2, 0, Math.PI / 2));
  canGeo.scale(0.82, 0.72, 1.9);
  var canopy = new THREE.Mesh(canGeo, track(new THREE.MeshStandardMaterial({
    color: 0x0c161e, roughness: 0.05, metalness: 0.75, envMapIntensity: 2.0,
    transparent: true, opacity: 0.94
  })));
  canopy.position.set(0, 0.5, -1.6); model.add(canopy); acParts.canopy = canopy;

  /* wings — swept, tapered planform (top view), extruded */
  function wingShape(span, rootC, tipC, sweep) {
    var s = new THREE.Shape();
    s.moveTo(0, 0); s.lineTo(span, -sweep);
    s.lineTo(span, -sweep - tipC); s.lineTo(0, -rootC); s.closePath(); return s;
  }
  var wGeo = track(new THREE.ExtrudeGeometry(wingShape(4.7, 2.0, 0.85, 1.15), { depth: 0.13, bevelEnabled: true, bevelThickness: 0.04, bevelSize: 0.05, bevelSegments: 2 }));
  wGeo.rotateX(Math.PI / 2);               /* lay flat: extrusion depth -> Y */
  var wingR = new THREE.Mesh(wGeo, paint);
  wingR.position.set(0.28, -0.18, -0.6); wingR.rotation.z = -0.06; model.add(wingR);
  var wingL = new THREE.Mesh(wGeo, paint);
  wingL.scale.x = -1; wingL.position.set(-0.28, -0.18, -0.6); wingL.rotation.z = 0.06; model.add(wingL);

  /* ailerons */
  var ailGeo = track(new THREE.BoxGeometry(1.5, 0.07, 0.4));
  acParts.ailR = new THREE.Mesh(ailGeo, navy);
  acParts.ailR.position.set(3.9, 0.08, 1.28); acParts.ailR.rotation.z = -0.06; model.add(acParts.ailR);
  acParts.ailL = new THREE.Mesh(ailGeo, navy);
  acParts.ailL.position.set(-3.9, 0.08, 1.28); acParts.ailL.rotation.z = 0.06; model.add(acParts.ailL);

  /* horizontal stabilizer + elevator */
  var hGeo = track(new THREE.ExtrudeGeometry(wingShape(1.9, 1.0, 0.5, 0.5), { depth: 0.09, bevelEnabled: false }));
  hGeo.rotateX(Math.PI / 2);
  var stabR = new THREE.Mesh(hGeo, paint); stabR.position.set(0.1, 0.12, 4.3); model.add(stabR);
  var stabL = new THREE.Mesh(hGeo, paint); stabL.scale.x = -1; stabL.position.set(-0.1, 0.12, 4.3); model.add(stabL);
  var elevGeo = track(new THREE.BoxGeometry(3.4, 0.06, 0.34));
  acParts.elev = new THREE.Mesh(elevGeo, navy);
  acParts.elev.position.set(0, 0.12, 5.28); model.add(acParts.elev);

  /* vertical fin + rudder */
  var finShape = new THREE.Shape();
  finShape.moveTo(0, 0); finShape.lineTo(-1.7, 0); finShape.lineTo(-0.6, 1.7); finShape.lineTo(0.0, 1.7); finShape.closePath();
  var finGeo = track(new THREE.ExtrudeGeometry(finShape, { depth: 0.1, bevelEnabled: false }));
  finGeo.rotateY(Math.PI / 2);
  var fin = new THREE.Mesh(finGeo, paint);
  fin.position.set(-0.05, 0.25, 5.15); model.add(fin);
  var rudGeo = track(new THREE.BoxGeometry(0.07, 1.4, 0.42));
  acParts.rudder = new THREE.Mesh(rudGeo, navy);
  acParts.rudder.position.set(0, 1.0, 5.5); model.add(acParts.rudder);

  /* engine nacelles */
  var nacGeo = track(new THREE.CylinderGeometry(0.34, 0.4, 1.7, 14));
  nacGeo.rotateX(Math.PI / 2);
  var exhaustMat = track(new THREE.MeshStandardMaterial({ color: 0x1c1e22, emissive: 0xff5a1e, emissiveIntensity: 0, roughness: 0.5, metalness: 0.7 }));
  [-1, 1].forEach(function (sgn) {
    var nac = new THREE.Mesh(nacGeo, dark);
    nac.position.set(0.86 * sgn, 0.28, 2.65); model.add(nac);
    var ex = new THREE.Mesh(track(new THREE.CircleGeometry(0.26, 12)), exhaustMat);
    ex.position.set(0.86 * sgn, 0.28, 3.52); model.add(ex);
    var intake = new THREE.Mesh(track(new THREE.CircleGeometry(0.3, 12)),
      track(new THREE.MeshStandardMaterial({ color: 0x05070a, roughness: 0.9 })));
    intake.rotation.y = Math.PI;
    intake.position.set(0.86 * sgn, 0.28, 1.79); model.add(intake);
  });
  acParts.exhaust = exhaustMat;

  /* landing gear (retractable) */
  var strutMat = dark;
  var wheelGeo = track(new THREE.CylinderGeometry(0.26, 0.26, 0.2, 12)); wheelGeo.rotateZ(Math.PI / 2);
  var wheelMat = track(new THREE.MeshStandardMaterial({ color: 0x111214, roughness: 0.9 }));
  function gearLeg(x, z, len) {
    var gGroup = new THREE.Group();
    var strut = new THREE.Mesh(track(new THREE.CylinderGeometry(0.06, 0.06, len, 6)), strutMat);
    strut.position.y = -len / 2; gGroup.add(strut);
    var wheel = new THREE.Mesh(wheelGeo, wheelMat);
    wheel.position.y = -len; gGroup.add(wheel);
    gGroup.position.set(x, -0.42, z);
    model.add(gGroup); return gGroup;
  }
  acParts.gear = [gearLeg(0, -2.6, 0.85), gearLeg(1.35, 0.5, 0.95), gearLeg(-1.35, 0.5, 0.95)];
  acParts.gearDown = 1;

  /* "MCKELVY" livery on both sides of the fuselage */
  function liveryTex(mirror) {
    return canvasTex(1024, 220, function (x, w, h) {
      x.clearRect(0, 0, w, h);
      x.save();
      if (mirror) { x.translate(w, 0); x.scale(-1, 1); }
      x.font = "600 118px Georgia, 'Times New Roman', serif";
      x.fillStyle = "#14243d"; x.textAlign = "center"; x.textBaseline = "middle";
      x.fillText("MCKELVY", w / 2, h / 2 - 12);
      x.fillStyle = "#a8935f"; x.fillRect(w * 0.14, h - 46, w * 0.72, 7);
      x.restore();
    });
  }
  [1, -1].forEach(function (sgn) {
    var mat = track(new THREE.MeshBasicMaterial({ map: liveryTex(false), transparent: true, polygonOffset: true, polygonOffsetFactor: -2 }));
    var plane = new THREE.Mesh(track(new THREE.PlaneGeometry(3.4, 0.73)), mat);
    plane.position.set(0.615 * sgn, 0.12, 0.35);
    /* front face outward on both sides; the plain texture reads correctly on each */
    plane.rotation.y = sgn > 0 ? Math.PI / 2 : -Math.PI / 2;
    model.add(plane);
  });

  /* navigation lights */
  function navLight(color, x, y, z) {
    var m = track(new THREE.MeshStandardMaterial({ color: color, emissive: color, emissiveIntensity: 2.4 }));
    var s = new THREE.Mesh(track(new THREE.SphereGeometry(0.09, 8, 6)), m);
    s.position.set(x, y, z); model.add(s); return m;
  }
  navLight(0xff2a1e, -4.95, -0.1, 0.4);   /* left red */
  navLight(0x2aff5e, 4.95, -0.1, 0.4);    /* right green */
  acParts.strobe = navLight(0xffffff, 0, 1.98, 5.2);

  /* cockpit interior — visible from the cockpit camera */
  var pit = new THREE.Group();
  var dash = new THREE.Mesh(track(new THREE.BoxGeometry(1.0, 0.3, 0.5)),
    track(new THREE.MeshStandardMaterial({ color: 0x15171a, roughness: 0.85 })));
  dash.position.set(0, 0.42, -2.35); pit.add(dash);
  acParts.dashCanvas = document.createElement("canvas");
  acParts.dashCanvas.width = 256; acParts.dashCanvas.height = 96;
  acParts.dashTex = track(new THREE.CanvasTexture(acParts.dashCanvas));
  var dashFace = new THREE.Mesh(track(new THREE.PlaneGeometry(0.92, 0.26)),
    track(new THREE.MeshBasicMaterial({ map: acParts.dashTex })));
  dashFace.position.set(0, 0.47, -2.09); dashFace.rotation.x = -0.35; pit.add(dashFace);
  var stick = new THREE.Mesh(track(new THREE.CylinderGeometry(0.03, 0.04, 0.5, 6)), dark);
  stick.position.set(0, 0.1, -1.75); pit.add(stick); acParts.stick = stick;
  model.add(pit);

  model.traverse(function (o) { if (o.isMesh) { o.castShadow = quality.shadow > 0; } });
  scene.add(aircraft);
}
function updateDash(ias, alt, bank, pitch) {
  var x = acParts.dashCanvas.getContext("2d");
  x.fillStyle = "#0b0d0f"; x.fillRect(0, 0, 256, 96);
  x.strokeStyle = "#2c3036"; x.strokeRect(4, 4, 248, 88);
  x.fillStyle = "#8dffab"; x.font = "16px monospace"; x.textAlign = "center";
  x.fillText(Math.round(ias) + " KT", 62, 40);
  x.fillText(Math.round(alt) + " FT", 194, 40);
  /* tiny attitude bar */
  x.save(); x.translate(128, 66); x.rotate(-bank);
  x.strokeStyle = "#c9b99a"; x.beginPath();
  x.moveTo(-36, pitch * 40); x.lineTo(36, pitch * 40); x.stroke(); x.restore();
  x.fillStyle = "#e8e0d0"; x.fillRect(126, 64, 4, 4);
  acParts.dashTex.needsUpdate = true;
}

/* ------------------------------------------------------------ flight physics */
var PH = {
  mass: 3200, S: 16.5, maxThrust: 21000,
  CL0: 0.32, CLa: 4.6, stallA: 0.30, CD0: 0.028, kInd: 0.052,
  rollP: 4.6, pitchP: 2.0, yawP: 1.1,
  rollD: 3.4, pitchD: 4.2, yawD: 2.6,
  stab: 2.4, weather: 1.4
};
var F = {   /* live flight state */
  pos: new THREE.Vector3(), vel: new THREE.Vector3(), quat: new THREE.Quaternion(),
  ang: new THREE.Vector3(),      /* body rates p(roll,-Z axis), q(pitch,+X), r(yaw,+Y) */
  throttle: 0.7, rpm: 0.7, gearDown: false, onGround: false, crashed: false,
  alpha: 0, ias: 0, stalled: false, gForce: 1
};
var _fw = new THREE.Vector3(), _up = new THREE.Vector3(), _rt = new THREE.Vector3(),
    _vB = new THREE.Vector3(), _force = new THREE.Vector3(), _tmpQ = new THREE.Quaternion(),
    _lift = new THREE.Vector3(), _side = new THREE.Vector3(), _qi = new THREE.Quaternion();

function spawn(mode) {
  F.crashed = false; F.onGround = false; F.stalled = false;
  F.ang.set(0, 0, 0);
  if (mode === "runway") {
    F.pos.set(RWY.x + RWY.len / 2 - 90, RWY.hgt + 1.35, RWY.z);
    F.quat.setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2); /* facing -X?  forward=-Z rotated +90° about Y -> -Z becomes -X... we want to face WEST along runway (toward -X) */
    F.vel.set(0, 0, 0); F.throttle = 0; F.gearDown = true; F.onGround = true;
  } else {
    F.pos.set(RWY.x + 5200, 520, RWY.z + 2600);
    var yaw = Math.atan2(-(RWY.x - F.pos.x), -(RWY.z - F.pos.z)); /* aim forward(-Z) at the airfield */
    F.quat.setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw);
    _fw.set(0, 0, -1).applyQuaternion(F.quat);
    F.vel.copy(_fw).multiplyScalar(78);
    F.throttle = 0.72; F.gearDown = false;
  }
  F.rpm = F.throttle;
  aircraft.position.copy(F.pos); aircraft.quaternion.copy(F.quat);
  if (CharSys) CharSys.onReset();
  hideCrashPanel();
}
/* face spawn heading correctly: forward is -Z; runway runs along X.
   Rotating -90° about Y maps -Z onto -X (westbound, toward runway numbers 27). */
function spawnFix() {
  /* helper kept for clarity; actual value set in spawn() below via setFromAxisAngle */
}

function physicsStep(dt, input) {
  if (F.crashed) {
    /* controlled tumble, decay to rest */
    F.vel.multiplyScalar(1 - 1.6 * dt);
    F.ang.multiplyScalar(1 - 1.2 * dt);
    F.pos.addScaledVector(F.vel, dt);
    var gh = heightAt(F.pos.x, F.pos.z) + 1.0;
    if (F.pos.y < gh) { F.pos.y = gh; F.vel.set(0, 0, 0); }
    integrateAttitude(dt);
    return;
  }
  /* body axes in world space */
  _fw.set(0, 0, -1).applyQuaternion(F.quat);
  _up.set(0, 1, 0).applyQuaternion(F.quat);
  _rt.set(1, 0, 0).applyQuaternion(F.quat);

  var speed = F.vel.length();
  F.ias = speed;
  var alt = F.pos.y;
  var rho = 1.225 * Math.exp(-Math.max(alt, 0) / 8500);
  var qbar = 0.5 * rho * speed * speed;
  var qn = clamp(qbar / 2800, 0, 1.6);           /* control authority scale */

  /* angle of attack & sideslip */
  _qi.copy(F.quat).invert();
  _vB.copy(F.vel).applyQuaternion(_qi);
  var fwdSpd = -_vB.z;
  F.alpha = speed > 4 ? Math.atan2(-_vB.y, Math.max(fwdSpd, 2)) : 0;
  var beta = speed > 4 ? Math.atan2(_vB.x, Math.max(fwdSpd, 2)) : 0;

  /* lift coefficient with stall drop-off */
  var a = F.alpha;
  var CL = PH.CL0 + PH.CLa * a;
  var stallEdge = Math.abs(a) > PH.stallA;
  var flatDrag = 0;
  if (stallEdge) {
    var over = Math.abs(a) - PH.stallA;
    CL *= Math.max(0, 1 - over * 3.2);            /* lift breaks down past the stall */
    flatDrag = Math.min(1.3, over * 2.2);         /* falling flat is very draggy */
  }
  F.stalled = (stallEdge && speed > 10) || (speed < 34 && !F.onGround && F.throttle < 0.9);
  CL = clamp(CL, -1.4, 1.75);

  /* forces */
  _force.set(0, -9.81 * PH.mass, 0);
  _force.addScaledVector(_fw, F.rpm * PH.maxThrust);
  if (speed > 2) {
    /* lift ⊥ velocity, in the plane of symmetry */
    _side.crossVectors(F.vel, _up).normalize();
    _lift.crossVectors(_side, F.vel).normalize();
    var Lf = clamp(qbar * PH.S * CL, -260000, 260000);
    _force.addScaledVector(_lift, Lf);
    var CD = PH.CD0 + PH.kInd * CL * CL + (F.gearDown ? 0.024 : 0) + flatDrag;
    _force.addScaledVector(F.vel, -qbar * PH.S * CD / Math.max(speed, 1));
    _force.addScaledVector(_rt, -beta * qbar * PH.S * 0.7);
    F.gForce = clamp(Lf / (9.81 * PH.mass), -3, 6);
  } else { F.gForce = 1; }

  /* control moments -> body angular rates */
  var ail = input.roll, elev = input.pitch, rud = input.yaw;
  if (settings.assist) {
    rud += beta * 1.9;                           /* auto-coordination */
    var bankAngle = Math.asin(clamp(-_rt.y, -1, 1));   /* + = right wing down */
    if (Math.abs(input.roll) < 0.05) ail += clamp(-bankAngle * 1.1, -0.5, 0.5); /* gentle auto-level */
    if (bankAngle > 1.1 && input.roll > 0) ail = Math.min(ail, 0);   /* bank limits */
    if (bankAngle < -1.1 && input.roll < 0) ail = Math.max(ail, 0);
    if (Math.abs(input.pitch) < 0.05) elev += clamp(-F.vel.y * 0.014, -0.18, 0.18); /* vario damper — holds level flight */
    if (F.alpha > PH.stallA * 0.8 && input.pitch > 0) elev = Math.min(elev, 0.1); /* stall protection */
    if (F.stalled) { elev -= 0.35; F.throttle = Math.max(F.throttle, 0.55); }
  }
  var au = 0.35 + qn;
  F.ang.z += (-ail * PH.rollP * au - F.ang.z * PH.rollD * au) * dt;      /* roll about body Z(fwd axis is -Z; sign folded into apply) */
  /* at very low airspeed the nose falls toward the flight path (pendulum stability) */
  var lowQ = 1 - clamp(speed / 42, 0, 1);
  F.ang.x += (elev * PH.pitchP * au - F.ang.x * PH.pitchD * au - a * PH.stab * Math.max(qn, 0.18) - clamp(a, -1, 1) * 1.5 * lowQ) * dt;
  F.ang.y += (-rud * PH.yawP * au - F.ang.y * PH.yawD * au - beta * PH.weather * qn - clamp(beta, -1, 1) * 0.8 * lowQ) * dt;
  F.ang.clampScalar(-2.6, 2.6);

  /* integrate linear */
  F.vel.addScaledVector(_force, dt / PH.mass);
  if (F.vel.length() > 260) F.vel.setLength(260);       /* overspeed guard */
  F.pos.addScaledVector(F.vel, dt);

  /* engine spool */
  F.rpm = dampf(F.rpm, F.throttle, 1.4, dt);

  integrateAttitude(dt);
  groundInteraction(dt, input);

  /* NaN guard — never let the sim die */
  if (!isFinite(F.pos.x + F.pos.y + F.pos.z + F.vel.x + F.vel.y + F.vel.z)) {
    spawn("air"); toast("FLIGHT STATE RECOVERED");
  }
}
function integrateAttitude(dt) {
  /* apply body rates: x=pitch axis(right), y=yaw axis(up), z=roll axis */
  var hx = F.ang.x * dt, hy = F.ang.y * dt, hz = F.ang.z * dt;
  _tmpQ.set(Math.sin(hx / 2), 0, 0, Math.cos(hx / 2)); F.quat.multiply(_tmpQ);
  _tmpQ.set(0, Math.sin(hy / 2), 0, Math.cos(hy / 2)); F.quat.multiply(_tmpQ);
  _tmpQ.set(0, 0, Math.sin(hz / 2), Math.cos(hz / 2)); F.quat.multiply(_tmpQ);
  F.quat.normalize();
}
function groundInteraction(dt, input) {
  var gh = heightAt(F.pos.x, F.pos.z);
  var wheelH = 1.35;
  var agl = F.pos.y - gh;
  var vs = F.vel.y;
  var overWater = gh < 0.5;
  if (overWater && F.pos.y < 1.0) { doCrash("water"); return; }
  if (agl > wheelH) { F.onGround = false; return; }

  var runway = onRunway(F.pos.x, F.pos.z);
  _up.set(0, 1, 0).applyQuaternion(F.quat);
  var bankOK = _up.y > 0.94;
  var gentle = vs > -6.5;
  var slowEnough = F.vel.length() < 110;

  if (!F.onGround) {
    /* touching down */
    var surfaceOK = runway || (terrainNormal(F.pos.x, F.pos.z).y > 0.985 && F.vel.length() < 46);
    if (!(gentle && bankOK && surfaceOK && slowEnough && F.gearDown)) { doCrash(runway ? "hard" : "terrain"); return; }
    toast(runway ? "TOUCHDOWN" : "DOWN IN THE FIELDS");
  }
  F.onGround = true;
  F.pos.y = gh + wheelH;
  if (F.vel.y < 0) F.vel.y = 0;
  /* rolling resistance (+ brakes when throttle is closed) as a real deceleration */
  var sp = F.vel.length();
  if (sp > 0.05) {
    var decel = (runway ? 0.18 : 2.4) + (F.throttle < 0.04 ? 3.4 : 0);
    var newSp = Math.max(0, sp - decel * dt);
    F.vel.multiplyScalar(newSp / sp);
  }
  /* level the aircraft to the strip, allow nose steering with rudder */
  F.ang.z = dampf(F.ang.z, 0, 8, dt);
  F.ang.x = dampf(F.ang.x, 0, 4, dt);
  F.ang.y += -input.yaw * clamp(sp * 0.06, 0.2, 1.6) * dt * 2.2;
  /* pin roll level on ground */
  _rt.set(1, 0, 0).applyQuaternion(F.quat);
  var bankAngle = Math.asin(clamp(-_rt.y, -1, 1));
  F.ang.z += bankAngle * 4 * dt;
  if (sp < 0.4 && F.throttle < 0.03) F.vel.set(0, 0, 0);
}
var crashPanel = null;
function doCrash(kind) {
  if (F.crashed) return;
  F.crashed = true;
  F.ang.set((Math.random() - 0.5) * 2, (Math.random() - 0.5) * 2, (Math.random() - 0.5) * 3);
  F.vel.multiplyScalar(0.25);
  if (CharSys) CharSys.onCrash();
  AUD.crash();
  setTimeout(function () {
    if (!F.crashed) return;
    crashPanel = openModal(
      "<h2>Flight ended</h2><p>" +
      (kind === "water" ? "The SC-1 is in the sea. The sea does not mind." :
       kind === "hard" ? "That arrival exceeded the landing gear's terms of service." :
       "The terrain declined to negotiate.") +
      "</p><div class='sf-row'>" +
      "<button class='primary' id='sfCrashReset'>RESET FLIGHT — AIRBORNE</button>" +
      "<button id='sfCrashRwy'>RESET ON RUNWAY</button></div>" +
      "<div class='sf-row'><a href='" + EXIT_URL + "'>EXIT TO SANDBOX</a></div>", "Flight ended");
    crashPanel.querySelector("#sfCrashReset").onclick = function () { closeModal(); spawn("air"); };
    crashPanel.querySelector("#sfCrashRwy").onclick = function () { closeModal(); spawn("runway"); };
  }, 1400);
}
function hideCrashPanel() { if (crashPanel) { closeModal(); crashPanel = null; } }

/* -------------------------------------------------------------------- input */
var Input = {
  keys: {}, roll: 0, pitch: 0, yaw: 0, thrKey: 0,
  padIndex: -1, padWasConnected: false,
  target: { roll: 0, pitch: 0, yaw: 0 }
};
function pollInput(dt) {
  var k = Input.keys, s = settings.sens;
  var tr = (k.KeyD ? 1 : 0) - (k.KeyA ? 1 : 0);
  var tp = (k.KeyS ? 1 : 0) - (k.KeyW ? 1 : 0);   /* S = pitch up (pull back) */
  var ty = (k.KeyE ? 1 : 0) - (k.KeyQ ? 1 : 0);
  if (k.ShiftLeft || k.ShiftRight) F.throttle = clamp(F.throttle + dt * 0.45, 0, 1);
  if (k.ControlLeft || k.ControlRight) F.throttle = clamp(F.throttle - dt * 0.5, 0, 1);

  /* gamepad */
  if (Input.padIndex >= 0) {
    var pads = navigator.getGamepads ? navigator.getGamepads() : [];
    var gp = pads[Input.padIndex];
    if (gp) {
      function dz(v) { return Math.abs(v) < 0.12 ? 0 : (v - Math.sign(v) * 0.12) / 0.88; }
      tr += dz(gp.axes[0] || 0);
      tp += dz(gp.axes[1] || 0);
      if (gp.axes.length > 2) ty += dz(gp.axes[2] || 0);
      var up2 = gp.buttons[7] ? gp.buttons[7].value : 0;
      var dn2 = gp.buttons[6] ? gp.buttons[6].value : 0;
      F.throttle = clamp(F.throttle + (up2 - dn2) * dt * 0.9, 0, 1);
      if (pressedOnce(gp, 0)) cycleCamera();
      if (pressedOnce(gp, 2)) { spawn("air"); toast("RESET"); }
      if (pressedOnce(gp, 9)) togglePause();
    } else { Input.padIndex = -1; toast("GAMEPAD DISCONNECTED"); }
  }
  tr = clamp(tr * s, -1, 1); tp = clamp(tp * s, -1, 1); ty = clamp(ty * s, -1, 1);
  Input.roll = dampf(Input.roll, tr, tr !== 0 ? 6.5 : 4.5, dt);
  Input.pitch = dampf(Input.pitch, tp, tp !== 0 ? 6.5 : 4.5, dt);
  Input.yaw = dampf(Input.yaw, ty, ty !== 0 ? 6.5 : 4.5, dt);
}
var padPrev = {};
function pressedOnce(gp, idx) {
  var now = gp.buttons[idx] && gp.buttons[idx].pressed;
  var key = gp.index + ":" + idx, was = padPrev[key];
  padPrev[key] = now;
  return now && !was;
}

/* ---------------------------------------------------- wing-rider character */
var CharSys = null, _prevUp = new THREE.Vector3(0,1,0);
function charTelemetry() {
  _rt.set(1,0,0).applyQuaternion(F.quat);
  _up.set(0,1,0).applyQuaternion(F.quat);
  return {
    rollRate: F.ang.z, pitchRate: F.ang.x, yawRate: F.ang.y,
    g: F.gForce, speed: F.ias,
    bank: Math.asin(clamp(-_rt.y, -1, 1)) + (_up.y < 0 ? Math.PI * Math.sign(-(-_rt.y)||1) * 0 : 0),
    upY: _up.y, onGround: F.onGround, crashed: F.crashed
  };
}
function initCharacter() {
  if (!window.StanhopeCharacter) return;   /* character script optional */
  try {
    CharSys = window.StanhopeCharacter.create({
      THREE: THREE,
      aircraftModel: acParts.model,
      uiRoot: UI.hud,
      getTelemetry: charTelemetry,
      getVelocity: function () { return F.vel; },
      isPaused: function () { return paused; },
      toast: toast
    });
  } catch (e) { crslConsole(e); CharSys = null; }
}
function crslConsole(e){ try{ console.error("CHARACTER INIT", e); }catch(x){} }

/* ------------------------------------------------------------------ cameras */
var Cam = {
  mode: settings.cam, blend: 0,
  pos: new THREE.Vector3(), look: new THREE.Vector3(),
  off: new THREE.Vector3(0, 4, 18), lookOff: new THREE.Vector3(0, 0, -8),
  orbitAz: 0.6, orbitEl: 0.28, orbitR: 17, drag: null,
  shakeT: 0
};
var _cp = new THREE.Vector3(), _cl = new THREE.Vector3(), _co = new THREE.Vector3();
function cycleCamera() {
  var order = CharSys ? ["chase", "character", "cockpit", "orbit"] : ["chase", "cockpit", "orbit"];
  Cam.mode = order[(order.indexOf(Cam.mode) + 1) % order.length];
  Cam.blend = 1;
  settings.cam = Cam.mode; saveSettings();
  UI.camChip.textContent = Cam.mode.toUpperCase();
  toast("CAMERA: " + Cam.mode.toUpperCase());
}
function updateCamera(dt) {
  var target = aircraft.position;
  if (Cam.mode === "cockpit") {
    _co.set(0, 0.66, -1.55).applyQuaternion(F.quat);
    _cp.copy(target).add(_co);
    /* subtle g response */
    _co.set(0, clamp((1 - F.gForce) * 0.05, -0.08, 0.08), 0).applyQuaternion(F.quat);
    _cp.add(_co);
    _cl.set(0, 0.62, -30).applyQuaternion(F.quat).add(target);
  } else if (Cam.mode === "character" && CharSys) {
    var boost = settings.actionCam ? CharSys.actionCamAmount() : 0;
    _co.set(3.2 + boost * 2.2, 1.9 + boost * 0.7, 6.4 + boost * 1.2).applyQuaternion(F.quat);
    _cp.copy(target).add(_co);
    var ghc = heightAt(_cp.x, _cp.z) + 1.4;
    if (_cp.y < ghc) _cp.y = ghc;
    CharSys.getCamFocus(_cl);
  } else if (Cam.mode === "chase") {
    var boostC = (CharSys && settings.actionCam) ? CharSys.actionCamAmount() : 0;
    _co.set(0 + boostC * 3.0, 2.7 + boostC * 0.6, 12.5 - boostC * 3.5).applyQuaternion(F.quat);
    _cp.copy(target).add(_co);
    var gh = heightAt(_cp.x, _cp.z) + 1.6;
    if (_cp.y < gh) _cp.y = gh;
    _cl.copy(target).addScaledVector(_fw, 9);
  } else {
    Cam.orbitAz += dt * 0.07;
    _cp.set(
      target.x + Math.cos(Cam.orbitAz) * Math.cos(Cam.orbitEl) * Cam.orbitR,
      target.y + Math.sin(Cam.orbitEl) * Cam.orbitR,
      target.z + Math.sin(Cam.orbitAz) * Math.cos(Cam.orbitEl) * Cam.orbitR);
    var gh2 = heightAt(_cp.x, _cp.z) + 1.2;
    if (_cp.y < gh2) _cp.y = gh2;
    _cl.copy(target);
  }
  /* damp the OFFSET from the aircraft, not the world position —
     otherwise the camera trails the aircraft by v/λ metres at speed */
  var stiff = Cam.mode === "chase" ? 5.2 : Cam.mode === "cockpit" ? 34 : 8;
  if (Cam.blend > 0) { Cam.blend = Math.max(0, Cam.blend - dt / 0.35); stiff = Math.min(stiff, 7); }
  _co.copy(_cp).sub(target);
  Cam.off.x = dampf(Cam.off.x, _co.x, stiff, dt);
  Cam.off.y = dampf(Cam.off.y, _co.y, stiff, dt);
  Cam.off.z = dampf(Cam.off.z, _co.z, stiff, dt);
  Cam.pos.copy(target).add(Cam.off);
  _co.copy(_cl).sub(target);
  Cam.lookOff.x = dampf(Cam.lookOff.x, _co.x, 11, dt);
  Cam.lookOff.y = dampf(Cam.lookOff.y, _co.y, 11, dt);
  Cam.lookOff.z = dampf(Cam.lookOff.z, _co.z, 11, dt);
  Cam.look.copy(target).add(Cam.lookOff);
  camera.position.copy(Cam.pos);
  camera.up.set(0, 1, 0);
  if (Cam.mode === "cockpit") { camera.up.copy(_up); }
  camera.lookAt(Cam.look);
}

/* -------------------------------------------------------------------- audio */
var AUD = (function () {
  var ctx = null, master = null, engineOsc = [], engineGain = null, engineFilter = null,
      windSrc = null, windGain = null, windFilter = null, stallOsc = null, stallGain = null,
      started = false;
  function ensure() {
    if (started || !settings.sound) return;
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      master = ctx.createGain(); master.gain.value = 0.5; master.connect(ctx.destination);
      /* engine: two detuned saws + sub sine through a lowpass */
      engineFilter = ctx.createBiquadFilter(); engineFilter.type = "lowpass"; engineFilter.frequency.value = 700;
      engineGain = ctx.createGain(); engineGain.gain.value = 0;
      engineFilter.connect(engineGain); engineGain.connect(master);
      [0, 7].forEach(function (det) {
        var o = ctx.createOscillator(); o.type = "sawtooth"; o.frequency.value = 80; o.detune.value = det;
        o.connect(engineFilter); o.start(); engineOsc.push(o);
      });
      var sub = ctx.createOscillator(); sub.type = "sine"; sub.frequency.value = 42;
      sub.connect(engineFilter); sub.start(); engineOsc.push(sub);
      /* wind: looped noise through bandpass */
      var len = ctx.sampleRate * 2, buf = ctx.createBuffer(1, len, ctx.sampleRate), d = buf.getChannelData(0);
      for (var i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
      windSrc = ctx.createBufferSource(); windSrc.buffer = buf; windSrc.loop = true;
      windFilter = ctx.createBiquadFilter(); windFilter.type = "bandpass"; windFilter.frequency.value = 500; windFilter.Q.value = 0.6;
      windGain = ctx.createGain(); windGain.gain.value = 0;
      windSrc.connect(windFilter); windFilter.connect(windGain); windGain.connect(master); windSrc.start();
      /* stall beeper */
      stallOsc = ctx.createOscillator(); stallOsc.type = "square"; stallOsc.frequency.value = 820;
      stallGain = ctx.createGain(); stallGain.gain.value = 0;
      stallOsc.connect(stallGain); stallGain.connect(master); stallOsc.start();
      started = true;
    } catch (e) { /* no audio support — stay silent */ }
  }
  return {
    unlock: ensure,
    update: function (dt, paused) {
      if (!started) return;
      var mgain = settings.sound && !paused ? 0.5 : 0.0;
      master.gain.value = dampf(master.gain.value, mgain, 6, dt);
      var f = 60 + F.rpm * 190;
      engineOsc[0].frequency.value = f; engineOsc[1].frequency.value = f * 1.007;
      engineOsc[2].frequency.value = 30 + F.rpm * 40;
      engineFilter.frequency.value = 400 + F.rpm * 1400;
      engineGain.gain.value = 0.055 + F.rpm * 0.13;
      var w = clamp(F.ias / 200, 0, 1.2);
      windFilter.frequency.value = 350 + F.ias * 9;
      windGain.gain.value = w * w * 0.5;
      stallGain.gain.value = F.stalled && !F.crashed ? (Math.sin(performance.now() * 0.02) > 0 ? 0.06 : 0) : 0;
    },
    crash: function () {
      if (!started || !settings.sound) return;
      try {
        var len = ctx.sampleRate * 0.7, buf = ctx.createBuffer(1, len, ctx.sampleRate), d = buf.getChannelData(0);
        for (var i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 2.2);
        var s = ctx.createBufferSource(); s.buffer = buf;
        var fl = ctx.createBiquadFilter(); fl.type = "lowpass"; fl.frequency.value = 320;
        var gg = ctx.createGain(); gg.gain.value = 0.9;
        s.connect(fl); fl.connect(gg); gg.connect(master); s.start();
      } catch (e) {}
    },
    click: function () {
      if (!started || !settings.sound) return;
      try {
        var o = ctx.createOscillator(), g2 = ctx.createGain();
        o.type = "sine"; o.frequency.value = 660; g2.gain.value = 0.08;
        g2.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.08);
        o.connect(g2); g2.connect(master); o.start(); o.stop(ctx.currentTime + 0.1);
      } catch (e) {}
    },
    suspend: function () { if (ctx && ctx.state === "running") ctx.suspend(); },
    resume: function () { if (ctx && ctx.state === "suspended" && settings.sound) ctx.resume(); },
    dispose: function () { if (ctx) try { ctx.close(); } catch (e) {} started = false; ctx = null; }
  };
})();

/* ------------------------------------------------------------------- HUD */
var hudT = 0;
function updateHUD(dt) {
  hudT += dt; if (hudT < 0.08) return; hudT = 0;
  var kt = Math.round(F.ias * 1.94384);
  var ft = Math.round(F.pos.y * 3.28084);
  var hdg = Math.round((Math.atan2(_fw.x, -_fw.z) * 180 / Math.PI + 360) % 360);
  var fpm = Math.round(F.vel.y * 196.85 / 50) * 50;
  UI.ias.textContent = kt; UI.altEl.textContent = ft;
  UI.hdg.textContent = (hdg < 100 ? (hdg < 10 ? "00" : "0") : "") + hdg + "°";
  UI.vsi.textContent = (fpm > 0 ? "+" : "") + fpm;
  UI.thrBar.style.transform = "scaleX(" + F.throttle.toFixed(2) + ")";
  if (F.stalled && !F.crashed) { UI.alert.style.display = ""; UI.alert.textContent = "STALL"; }
  else if (paused) { UI.alert.style.display = ""; UI.alert.textContent = "PAUSED"; UI.alert.style.animation = "none"; }
  else { UI.alert.style.display = "none"; UI.alert.style.animation = ""; }
  /* attitude ball */
  var x = UI.horizon.getContext("2d");
  var r = 59;
  var bank = Math.asin(clamp(-_rt.y, -1, 1));
  var pitch = Math.asin(clamp(-_fw.y, -1, 1));
  x.clearRect(0, 0, 118, 118);
  x.save(); x.translate(59, 59); x.rotate(bank);
  var off = clamp(pitch * 2.2, -1.1, 1.1) * r;
  x.fillStyle = "#3a6ea5"; x.fillRect(-r, -r * 2 + off, r * 2, r * 2);
  x.fillStyle = "#6b5233"; x.fillRect(-r, off, r * 2, r * 2);
  x.strokeStyle = "#e8e0d0"; x.lineWidth = 1.4;
  x.beginPath(); x.moveTo(-r, off); x.lineTo(r, off); x.stroke();
  x.restore();
  /* fixed reference wings */
  x.strokeStyle = "#c9b99a"; x.lineWidth = 2.5;
  x.beginPath(); x.moveTo(24, 59); x.lineTo(48, 59); x.moveTo(70, 59); x.lineTo(94, 59); x.stroke();
  x.fillStyle = "#c9b99a"; x.fillRect(57, 57, 4, 4);
  updateDash(kt, ft, bank, pitch);
}

/* ---------------------------------------------------- aircraft visual state */
var strobeT = 0;
function updateAircraftVisuals(dt) {
  aircraft.position.copy(F.pos);
  aircraft.quaternion.copy(F.quat);
  acParts.ailR.rotation.x = dampf(acParts.ailR.rotation.x, -Input.roll * 0.5, 10, dt);
  acParts.ailL.rotation.x = dampf(acParts.ailL.rotation.x, Input.roll * 0.5, 10, dt);
  acParts.elev.rotation.x = dampf(acParts.elev.rotation.x, -Input.pitch * 0.5, 10, dt);
  acParts.rudder.rotation.y = dampf(acParts.rudder.rotation.y, -Input.yaw * 0.55, 10, dt);
  acParts.stick.rotation.z = -Input.roll * 0.3;
  acParts.stick.rotation.x = -Input.pitch * 0.3;
  acParts.exhaust.emissiveIntensity = F.rpm * 1.6;
  /* automatic gear */
  var agl = F.pos.y - heightAt(F.pos.x, F.pos.z);
  var wantDown = F.onGround || (agl < 150 && F.ias < 95);
  F.gearDown = wantDown;
  acParts.gearDown = dampf(acParts.gearDown, wantDown ? 1 : 0, 2.2, dt);
  acParts.gear.forEach(function (gLeg) {
    gLeg.rotation.x = (1 - acParts.gearDown) * 1.5;
    gLeg.visible = acParts.gearDown > 0.03;
  });
  strobeT += dt;
  acParts.strobe.emissiveIntensity = (strobeT % 1.4) < 0.08 ? 5 : 0.15;
}

/* -------------------------------------------------------------- pause/menus */
var paused = false, running = false, helpShown = false;
function togglePause(force) {
  var want = force != null ? force : !paused;
  if (want === paused) return;
  paused = want;
  if (paused) {
    AUD.suspend();
    var p = openModal(
      "<h2>Paused</h2><p>SC-1 MCKELVY · Scoreborn Flight</p>" +
      "<div class='sf-row'><button class='primary' id='sfResume'>RESUME</button>" +
      "<button id='sfR1'>RESET — AIRBORNE</button><button id='sfR2'>RESET — RUNWAY</button></div>" +
      "<div class='sf-row'>" +
      "<button id='sfAssistB' aria-pressed='" + settings.assist + "'>ASSIST: " + (settings.assist ? "ON" : "OFF") + "</button>" +
      "<button id='sfSoundB' aria-pressed='" + settings.sound + "'>SOUND: " + (settings.sound ? "ON" : "OFF") + "</button>" +
      (CharSys ? "<button id='sfActCamB' aria-pressed='" + settings.actionCam + "'>ACTION CAM: " + (settings.actionCam ? "ON" : "OFF") + "</button>" : "") +
      "</div><div class='sf-row' id='sfQRow'></div>" +
      "<table class='sf-kbd'><tr><td>W / S</td><td>pitch down / pull up</td></tr>" +
      "<tr><td>A / D</td><td>roll left / right</td></tr><tr><td>Q / E</td><td>yaw left / right</td></tr>" +
      "<tr><td>SHIFT / CTRL</td><td>throttle up / down</td></tr><tr><td>C · R · M · F</td><td>camera · reset · sound · fullscreen</td></tr>" +
      "<tr><td>1 · 2 · 3 · 4</td><td>rider: stand · sit · front flip · backflip</td></tr>" +
      "<tr><td>P / ESC</td><td>pause</td></tr><tr><td>GAMEPAD</td><td>left stick fly · triggers throttle · A camera · X reset</td></tr></table>" +
      "<div class='sf-row'><a href='" + EXIT_URL + "'>EXIT TO SANDBOX</a>" +
      "<a href='" + STORE_URL + "'>STORE</a><a href='" + HOME_URL + "'>GATEWAY</a></div>", "Pause menu");
    p.querySelector("#sfResume").onclick = function () { togglePause(false); };
    p.querySelector("#sfR1").onclick = function () { togglePause(false); spawn("air"); };
    p.querySelector("#sfR2").onclick = function () { togglePause(false); spawn("runway"); };
    p.querySelector("#sfAssistB").onclick = function () {
      settings.assist = !settings.assist; saveSettings();
      this.textContent = "ASSIST: " + (settings.assist ? "ON" : "OFF");
      this.setAttribute("aria-pressed", settings.assist);
      UI.assistChip.textContent = settings.assist ? "ON" : "OFF";
    };
    var acb = p.querySelector("#sfActCamB");
    if (acb) acb.onclick = function () {
      settings.actionCam = !settings.actionCam; saveSettings();
      this.textContent = "ACTION CAM: " + (settings.actionCam ? "ON" : "OFF");
      this.setAttribute("aria-pressed", settings.actionCam);
    };
    p.querySelector("#sfSoundB").onclick = function () {
      setSound(!settings.sound);
      this.textContent = "SOUND: " + (settings.sound ? "ON" : "OFF");
      this.setAttribute("aria-pressed", settings.sound);
    };
    var qr = p.querySelector("#sfQRow");
    ["low", "medium", "high", "ultra"].forEach(function (k) {
      var b = document.createElement("button");
      b.textContent = k.toUpperCase();
      b.setAttribute("aria-pressed", String(qKey === k));
      b.onclick = function () {
        settings.quality = k; saveSettings(); applyQuality(k);
        qr.querySelectorAll("button").forEach(function (bb) { bb.setAttribute("aria-pressed", "false"); });
        b.setAttribute("aria-pressed", "true");
      };
      qr.appendChild(b);
    });
  } else { closeModal(); AUD.resume(); }
}
function setSound(on) {
  settings.sound = on; saveSettings();
  UI.sndChip.textContent = on ? "ON" : "OFF";
  if (on) { AUD.unlock(); AUD.resume(); } 
}
function toggleHelp() {
  helpShown = !helpShown;
  UI.help.style.display = helpShown ? "" : "none";
  settings.helpSeen = true; saveSettings();
}

/* ------------------------------------------------------------ load sequence */
function stage(label, pct) {
  UI.loadStage.textContent = label;
  UI.loadBar.style.transform = "scaleX(" + (pct / 100) + ")";
  UI.loadPB.setAttribute("aria-valuenow", String(Math.round(pct)));
  /* yield to the compositor, but never stall if rAF is throttled (hidden tab) */
  return new Promise(function (res) {
    var done = false;
    function fin() { if (!done) { done = true; res(); } }
    requestAnimationFrame(function () { requestAnimationFrame(fin); });
    setTimeout(fin, 160);
  });
}
function boot() {
  buildUI();
  if (typeof THREE === "undefined") {
    fatal("The simulator could not load", "The rendering library failed to download. Check your connection and reload the page.");
    return;
  }
  var isTouchOnly = matchMedia("(pointer:coarse)").matches && !matchMedia("(pointer:fine)").matches;
  if (isTouchOnly) {
    UI.load.style.display = "none";
    fatal("Designed for keyboard or gamepad",
      "The full SC-1 flight experience needs a desktop or laptop with a keyboard or gamepad. " +
      "The rest of Scoreborn works beautifully here — the simulator will wait for you.");
    return;
  }
  try { initRenderer(); }
  catch (e) {
    fatal("Graphics unavailable", "WebGL could not be initialised on this device or browser, so the SC-1 stays in the hangar. A current version of Chrome, Edge, Firefox, or Safari on hardware with GPU access will fly.");
    return;
  }
  renderer.domElement.addEventListener("webglcontextlost", function (ev) {
    ev.preventDefault(); running = false;
    fatal("Graphics context lost", "The browser released the GPU. Reload the page to restart the flight.");
  });

  var seq = Promise.resolve()
    .then(function () { return stage("ATMOSPHERE", 8); }).then(buildSky)
    .then(function () { return stage("TERRAIN — CARVING THE COAST", 22); }).then(buildTerrain)
    .then(function () { return stage("OCEAN", 44); }).then(buildOcean)
    .then(function () { return stage("FORESTS", 58); }).then(buildForest)
    .then(function () { return stage("AIRFIELD", 72); }).then(buildAirfield)
    .then(function () { return stage("WEATHER", 82); }).then(buildClouds)
    .then(function () { return stage("ROLLING OUT THE SC-1", 90); }).then(buildAircraft)
    .then(function () { return stage("WING RIDER", 96); }).then(initCharacter)
    .then(function () { return stage("READY", 100); })
    .then(function () {
      spawn("air");
      Cam.pos.copy(F.pos).add(new THREE.Vector3(0, 4, 18));
      Cam.look.copy(F.pos);
      UI.camChip.textContent = Cam.mode.toUpperCase();
      UI.assistChip.textContent = settings.assist ? "ON" : "OFF";
      UI.sndChip.textContent = settings.sound ? "ON" : "OFF";
      UI.load.style.opacity = "0";
      setTimeout(function () { UI.load.style.display = "none"; }, 550);
      UI.title.style.opacity = "1";
      running = true;
      startLoop();
      var begin = function () {
        removeEventListener("keydown", begin, true);
        removeEventListener("pointerdown", begin, true);
        UI.title.style.opacity = "0";
        setTimeout(function () { UI.title.style.display = "none"; }, 700);
        UI.hud.style.display = "";
        if (!settings.helpSeen) { helpShown = true; UI.help.style.display = ""; setTimeout(function () { if (helpShown) toggleHelp(); }, 9000); }
        AUD.unlock(); AUD.resume();
      };
      addEventListener("keydown", begin, true);
      addEventListener("pointerdown", begin, true);
    })
    .catch(function (err) {
      fatal("The simulator hit turbulence while loading",
        "Something failed during world construction. Reloading usually clears it. <span style='opacity:.6'>(" +
        String(err && err.message || err).slice(0, 120) + ")</span>");
    });
  return seq;
}

/* --------------------------------------------------------------- main loop */
var rafId = 0, lastT = 0, acc = 0, FIXED = 1 / 120;
var fpsAvg = 60, fpsLowT = 0, autoDropped = 0;
function startLoop() {
  lastT = performance.now();
  rafId = requestAnimationFrame(tick);
}
function tick(now) {
  if (!running) return;
  rafId = requestAnimationFrame(tick);
  var dt = Math.min((now - lastT) / 1000, 0.1);
  lastT = now;
  if (!paused) {
    pollInput(dt);
    acc += dt;
    var steps = 0;
    while (acc >= FIXED && steps < 6) { physicsStep(FIXED, Input); acc -= FIXED; steps++; }
    if (steps === 6) acc = 0;
    updateAircraftVisuals(dt);
    if (CharSys) CharSys.update(dt, camera, now);
    updateCamera(dt);
    if (oceanMat) {
      oceanMat.uniforms.uTime.value = now / 1000;
      oceanMat.uniforms.uCam.value.copy(camera.position);
    }
    if (cloudGroup) cloudGroup.position.x = (now / 1000) * 1.7 % 4000;
    if (sunLight) { sunLight.position.copy(F.pos).addScaledVector(SUN_DIR, 7000); sunLight.target.position.copy(F.pos); sunLight.target.updateMatrixWorld(); }
    updateHUD(dt);
  }
  AUD.update(dt, paused);
  renderer.render(scene, camera);
  /* adaptive quality: sustained low fps steps the render scale down */
  var fps = 1 / Math.max(dt, 0.0001);
  fpsAvg = fpsAvg * 0.95 + fps * 0.05;
  if (fpsAvg < 42 && !paused) {
    fpsLowT += dt;
    if (fpsLowT > 5 && autoDropped < 3) {
      fpsLowT = 0; autoDropped++;
      var pr = renderer.getPixelRatio() * 0.82;
      renderer.setPixelRatio(Math.max(pr, 0.55)); resize();
      toast("PERFORMANCE — RENDER SCALE REDUCED");
    }
  } else { fpsLowT = 0; }
}

/* ------------------------------------------------------------------ events */
function onKey(ev) {
  if (ev.type === "keydown") {
    if (ev.code === "Escape") {
      if (modal.node && !paused) { closeModal(); return; }
      togglePause(); return;
    }
    if (ev.code === "KeyP") { togglePause(); return; }
    if (paused) return;
    Input.keys[ev.code] = true;
    if (CharSys && !modal.node && /^Digit[1-4]$/.test(ev.code) && !/INPUT|TEXTAREA|SELECT/.test(ev.target && ev.target.tagName || "")) {
      CharSys.action({Digit1:"stand",Digit2:"sit",Digit3:"frontflip",Digit4:"backflip"}[ev.code]);
    }
    if (ev.code === "KeyC") cycleCamera();
    if (ev.code === "KeyR") { spawn("air"); toast("RESET"); }
    if (ev.code === "KeyM") setSound(!settings.sound);
    if (ev.code === "KeyH") toggleHelp();
    if (ev.code === "KeyF") {
      if (document.fullscreenElement) document.exitFullscreen();
      else if (root.requestFullscreen) root.requestFullscreen();
    }
    if (["KeyW","KeyS","KeyA","KeyD","KeyQ","KeyE","Space","ShiftLeft","ControlLeft"].indexOf(ev.code) >= 0) ev.preventDefault();
  } else {
    Input.keys[ev.code] = false;
  }
}
addEventListener("keydown", onKey);
addEventListener("keyup", onKey);
addEventListener("resize", resize);
addEventListener("gamepadconnected", function (ev) {
  Input.padIndex = ev.gamepad.index;
  toast("GAMEPAD CONNECTED — " + (ev.gamepad.id || "").slice(0, 24));
});
addEventListener("gamepaddisconnected", function () { Input.padIndex = -1; });
document.addEventListener("visibilitychange", function () {
  if (document.hidden && running && !paused) togglePause(true);
});
/* orbit camera drag */
root.addEventListener("pointerdown", function (ev) {
  if (Cam.mode !== "orbit" || modal.node) return;
  Cam.drag = { x: ev.clientX, y: ev.clientY, az: Cam.orbitAz, el: Cam.orbitEl };
});
addEventListener("pointermove", function (ev) {
  if (!Cam.drag) return;
  Cam.orbitAz = Cam.drag.az + (ev.clientX - Cam.drag.x) * 0.006;
  Cam.orbitEl = clamp(Cam.drag.el + (ev.clientY - Cam.drag.y) * 0.004, -0.1, 1.2);
});
addEventListener("pointerup", function () { Cam.drag = null; });

/* ---------------------------------------------------------------- teardown */
function teardown() {
  running = false;
  if (CharSys) { try { CharSys.dispose(); } catch (e) {} CharSys = null; }
  cancelAnimationFrame(rafId);
  AUD.dispose();
  try {
    if (scene) scene.traverse(function (o) {
      if (o.geometry) o.geometry.dispose();
      if (o.material) {
        (Array.isArray(o.material) ? o.material : [o.material]).forEach(function (m) {
          for (var k in m) { if (m[k] && m[k].isTexture) m[k].dispose(); }
          m.dispose();
        });
      }
    });
    disposables.forEach(function (d) { if (d && d.dispose) try { d.dispose(); } catch (e) {} });
    if (envRT) envRT.dispose();
    if (renderer) renderer.dispose();
  } catch (e) {}
}
addEventListener("pagehide", teardown);
root.addEventListener("click", function (ev) {
  var a = ev.target.closest ? ev.target.closest("a[href]") : null;
  if (a) { teardown(); }   /* navigation proceeds; loops and audio are already gone */
});

/* opt-in debug hook for QA (only with #sfdebug in the URL; never in normal use) */
if (/sfdebug/.test(location.href)) {
  window.SF_DEBUG = {
    F: F, Input: Input, Cam: Cam, spawn: spawn,
    parts: function () { return acParts; }, camera: function () { return camera; },
    char: function () { return CharSys; },
    step: function (n, inp) {
      for (var i = 0; i < (n || 1); i++) physicsStep(FIXED, inp || Input);
      return { pos: F.pos.toArray(), vel: F.vel.toArray(), ias: F.ias, alpha: F.alpha, stalled: F.stalled, crashed: F.crashed, onGround: F.onGround };
    },
    height: heightAt
  };
}

boot();
})();
